import { useEffect, useRef, useState } from "react";

export type LoaderPhase = "loading" | "completing" | "done" | "hidden";

const CREEP_INTERVAL_MS = 120;
const CREEP_CEILING = 90;
const CREEP_RATE = 0.025;
const COMPLETE_STEP = 4;
const COMPLETE_INTERVAL_MS = 24;
// Long enough for the stat count-up (900ms) to land before the fade starts.
const DONE_HOLD_MS = 1300;

export const useLoaderProgress = (
    ready: boolean,
    failed: boolean,
    statusCount: number
): { progress: number; phase: LoaderPhase; statusIndex: number } => {
    const [progress, setProgress] = useState(0);
    const [phase, setPhase] = useState<LoaderPhase>("loading");
    const [statusIndex, setStatusIndex] = useState(0);
    const phaseRef = useRef<LoaderPhase>("loading");
    phaseRef.current = phase;

    useEffect(() => {
        if (failed) {
            setPhase("hidden");
            return;
        }
        if (ready && phaseRef.current === "loading") {
            setPhase("completing");
        }
        if (!ready && !failed && phaseRef.current !== "loading") {
            setProgress(0);
            setStatusIndex(0);
            setPhase("loading");
        }
    }, [ready, failed]);

    useEffect(() => {
        if (phase !== "loading") return;
        const timer = window.setInterval(() => {
            setProgress((current) => current + (CREEP_CEILING - current) * CREEP_RATE);
        }, CREEP_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [phase]);

    useEffect(() => {
        if (phase !== "loading" || statusCount < 2) return;
        const timer = window.setInterval(() => {
            setStatusIndex((current) => (current + 1) % statusCount);
        }, 1600);
        return () => window.clearInterval(timer);
    }, [phase, statusCount]);

    useEffect(() => {
        if (phase !== "completing") return;
        const timer = window.setInterval(() => {
            setProgress((current) => {
                const next = current + COMPLETE_STEP;
                if (next >= 100) {
                    window.clearInterval(timer);
                    setPhase("done");
                    return 100;
                }
                return next;
            });
        }, COMPLETE_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [phase]);

    useEffect(() => {
        if (phase !== "done") return;
        const timer = window.setTimeout(() => setPhase("hidden"), DONE_HOLD_MS);
        return () => window.clearTimeout(timer);
    }, [phase]);

    return { progress, phase, statusIndex };
};
